import debounce from 'lodash/debounce'
import axios from 'axios'

import { initRadioGroup } from '../../components/radio_group'
import { getApiHeaders } from '../../utils/api_helper'

type VoucherState = 'idle' | 'loading' | 'valid' | 'invalid'

interface VoucherValidationResponse {
  success: 0 | 1
  error_message?: string
  discount_amount?: number
  voucher_type?: string
  remaining_amount?: number | string
}

function buildPaymentUrl(baseUrl: string, voucherCode?: string) {
  const url = new URL(baseUrl)
  if (voucherCode) {
    url.searchParams.set('voucherCode', voucherCode)
  }
  return url.toString()
}

function setVoucherState(state: VoucherState) {
  const spinner = document.getElementById('voucher-validation-spinner')
  const valid = document.getElementById('voucher-validation-valid')
  const invalid = document.getElementById('voucher-validation-invalid')
  const message = document.getElementById('voucher-validation-message')

  spinner?.classList.toggle('d-none', state !== 'loading')
  valid?.classList.toggle('d-none', state !== 'valid')
  invalid?.classList.toggle('d-none', state !== 'invalid')

  if (message) {
    if (state === 'valid') {
      message.textContent = 'Voucher applied'
      message.className = 'form-text ms-2 text-success'
    } else if (state === 'invalid') {
      message.textContent = 'Invalid voucher code'
      message.className = 'form-text ms-2 text-danger'
    } else {
      message.textContent = ''
      message.className = 'form-text d-none'
    }
  }
}

function initPaymentDetail() {
  const paymentMethodRadioGroup = document.querySelector<HTMLElement>(
    '#application-payment-detail .radio-group',
  )
  const voucherSection = document.querySelector<HTMLElement>(
    '[data-payment-code]',
  )
  const paymentCode = voucherSection?.dataset.paymentCode ?? null
  const paymentVoucherInput = document.querySelector<HTMLInputElement>(
    '#payment_voucher_code',
  )
  const makePaymentButton = document.querySelector<HTMLAnchorElement>(
    '#deposits_redirect_url',
  )
  const copyUrlButton =
    document.querySelector<HTMLButtonElement>('#deposits_copy_url')

  if (!paymentMethodRadioGroup) {
    return
  }

  const applicationId =
    document.getElementById('application-id')?.dataset.applicationId

  let voucherIsVerifiedValid = false
  let voucherRequestSeq = 0
  let voucherPaymentInFlight = false

  function setMakePaymentButtonDisabled(disabled: boolean) {
    if (!makePaymentButton) return
    makePaymentButton.classList.toggle('disabled', disabled)
    makePaymentButton.setAttribute('aria-disabled', String(disabled))
    if (disabled) {
      makePaymentButton.removeAttribute('href')
    }
  }

  function updateButtons(voucherCode?: string) {
    const selectedRadio =
      paymentMethodRadioGroup?.querySelector<HTMLInputElement>(
        'input[type=radio]:checked',
      )
    if (!selectedRadio) return

    const dataElement =
      selectedRadio.parentElement?.querySelector<HTMLElement>('[data-href]')
    if (!dataElement) return

    const url = buildPaymentUrl(dataElement.dataset.href!, voucherCode)
    makePaymentButton?.setAttribute('href', url)
    copyUrlButton?.setAttribute('data-copy-clipboard', url)
    const paymentLink = document.getElementById('deposits_send_url')
    const encodedPaymentUrl = encodeURIComponent(url)
    paymentLink?.setAttribute(
      'href',
      `${paymentLink.getAttribute('href')}&payment_url=${encodedPaymentUrl}`,
    )
  }

  async function handleFullVoucherPaymentClick(e: Event) {
    e.preventDefault()
    // Guard against rapid successive clicks redeeming the voucher more than
    // once: the payment POST plus the page reload leave a window where the
    // button still receives clicks, so a second click would fire a duplicate
    // request. The flag is set synchronously before the await, so it also
    // short-circuits any same-tick re-entrant invocation. See PP-3773.
    if (voucherPaymentInFlight) return
    if (!applicationId || !paymentCode) return

    const voucherCode = paymentVoucherInput?.value.trim() ?? ''
    voucherPaymentInFlight = true
    setMakePaymentButtonDisabled(true)

    try {
      const resp = await axios.post(
        `/api/v1/application/${applicationId}/voucher_payment`,
        { voucher_code: voucherCode, payment_code: paymentCode },
        { headers: getApiHeaders() },
      )
      if (resp.data?.success === 1) {
        // Success navigates away; keep the guard locked so the in-flight
        // button cannot fire another request before the reload completes.
        const url = new URL(window.location.href)
        url.searchParams.delete('pay')
        window.location.href = url.toString()
      } else {
        voucherPaymentInFlight = false
        setVoucherState('invalid')
        const message = document.getElementById('voucher-validation-message')
        if (message) {
          message.textContent =
            resp.data?.error_message || 'Payment failed. Please try again.'
          message.className = 'form-text ms-2 text-danger'
        }
        setMakePaymentButtonDisabled(false)
      }
    } catch {
      voucherPaymentInFlight = false
      setVoucherState('invalid')
      const message = document.getElementById('voucher-validation-message')
      if (message) {
        message.textContent = 'Unable to process payment. Please try again.'
        message.className = 'form-text ms-2 text-danger'
      }
      setMakePaymentButtonDisabled(false)
    }
  }

  function setFullVoucherMode(enabled: boolean, voucherCode?: string) {
    if (enabled) {
      makePaymentButton?.removeAttribute('href')
      makePaymentButton?.addEventListener(
        'click',
        handleFullVoucherPaymentClick,
      )
    } else {
      makePaymentButton?.removeEventListener(
        'click',
        handleFullVoucherPaymentClick,
      )
      updateButtons(voucherCode || undefined)
    }
  }

  async function validateVoucher(voucherCode: string) {
    if (!applicationId || !paymentCode) return

    const seq = ++voucherRequestSeq
    setVoucherState('loading')
    setMakePaymentButtonDisabled(true)
    voucherIsVerifiedValid = false

    try {
      const resp = await axios.post<VoucherValidationResponse>(
        `/api/v1/application/${applicationId}/validate_voucher`,
        { voucher_code: voucherCode, payment_code: paymentCode },
        { headers: getApiHeaders() },
      )
      if (seq !== voucherRequestSeq) return

      const data = resp.data
      if (data?.success === 1) {
        setVoucherState('valid')
        voucherIsVerifiedValid = true
        setMakePaymentButtonDisabled(false)

        if (
          data.remaining_amount != null &&
          data.remaining_amount !== '' &&
          Number(data.remaining_amount) === 0
        ) {
          setFullVoucherMode(true)
        } else {
          setFullVoucherMode(false, voucherCode)
        }
      } else {
        setVoucherState('invalid')
        setMakePaymentButtonDisabled(true)
      }
    } catch {
      if (seq !== voucherRequestSeq) return
      setVoucherState('invalid')
      setMakePaymentButtonDisabled(true)
    }
  }

  const debouncedValidate = debounce((voucherCode: string) => {
    validateVoucher(voucherCode)
  }, 800)

  initRadioGroup(paymentMethodRadioGroup, () => {
    const voucherCode = paymentVoucherInput?.value
    updateButtons(voucherIsVerifiedValid ? voucherCode : undefined)
  })

  paymentVoucherInput?.addEventListener('input', () => {
    const voucherCode = paymentVoucherInput.value.trim()

    if (!voucherCode) {
      debouncedValidate.cancel()
      setVoucherState('idle')
      voucherIsVerifiedValid = false
      setFullVoucherMode(false)
      setMakePaymentButtonDisabled(false)
      updateButtons(undefined)
      return
    }

    setMakePaymentButtonDisabled(true)
    voucherIsVerifiedValid = false
    setVoucherState('idle')
    debouncedValidate(voucherCode)
  })
}

export { initPaymentDetail }
